/**
 * Header Dropdown Portal
 * Move dropdowns para o body para evitar conflito de blur com o header
 */

(function() {
  'use strict';

  const CONFIG = {
    portalId: 'HeaderDropdownPortal',
    portalGap: 10,
    closeOnScrollThreshold: 5,
    transitionDuration: 200,
    hoverDelay: 80 // Delay antes de fechar no hover
  };

  let portal = null;
  let activeDropdown = null;
  let activeTrigger = null;
  let scrollStartY = 0;
  let isTransitioning = false;
  let hoverTimeout = null;
  let isHoveringPortal = false;
  let isHoveringTrigger = false;

  // ============================================================
  // CRIAR PORTAL
  // ============================================================
  function createPortal() {
    if (document.getElementById(CONFIG.portalId)) {
      portal = document.getElementById(CONFIG.portalId);
      return;
    }

    portal = document.createElement('div');
    portal.id = CONFIG.portalId;
    portal.className = 'header-dropdown-portal';
    portal.setAttribute('aria-hidden', 'true');
    document.body.appendChild(portal);

    // Fechar ao clicar fora
    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('keydown', handleKeydown);

    // Hover no portal
    portal.addEventListener('mouseenter', () => {
      isHoveringPortal = true;
      clearTimeout(hoverTimeout);
    });

    portal.addEventListener('mouseleave', () => {
      isHoveringPortal = false;
      scheduleClose();
    });
  }

  // ============================================================
  // POSICIONAR PORTAL
  // ============================================================
  function positionPortal(trigger) {
    if (!portal || !trigger) return;

    const triggerRect = trigger.getBoundingClientRect();
    const header = document.getElementById('MainHeader');
    const headerRect = header ? header.getBoundingClientRect() : { bottom: 0 };

    // Posição fixa baseada no viewport
    portal.style.position = 'fixed';
    portal.style.top = `${headerRect.bottom + CONFIG.portalGap}px`;
    portal.style.left = `${triggerRect.left}px`;
    portal.style.minWidth = `${Math.max(triggerRect.width, 200)}px`;
  }

  // ============================================================
  // ABRIR DROPDOWN
  // ============================================================
  function openDropdown(trigger, content) {
    // Cancela qualquer fechamento pendente
    clearTimeout(hoverTimeout);
    
    // Se já está aberto o mesmo dropdown, não faz nada
    if (activeDropdown === content && !isTransitioning) {
      return;
    }

    // Se está em transição, espera
    if (isTransitioning) {
      setTimeout(() => openDropdown(trigger, content), 50);
      return;
    }

    // Fecha o anterior sem animação se estiver trocando
    if (activeDropdown && activeDropdown !== content) {
      resetState();
    }

    isTransitioning = true;
    activeTrigger = trigger;
    activeDropdown = content;
    scrollStartY = window.scrollY;

    // Clona o conteúdo para o portal
    const clone = content.cloneNode(true);
    clone.classList.add('dropdown-portal-content');
    clone.style.display = 'block';
    clone.style.opacity = '0';
    clone.style.transform = 'translateY(-8px)';

    // Limpa e adiciona ao portal
    portal.innerHTML = '';
    portal.appendChild(clone);
    portal.classList.add('is-active');
    portal.setAttribute('aria-hidden', 'false');

    // Posiciona
    positionPortal(trigger);

    // Adiciona listener de scroll
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Anima entrada
    requestAnimationFrame(() => {
      clone.style.transition = `opacity ${CONFIG.transitionDuration}ms ease, transform ${CONFIG.transitionDuration}ms ease`;
      clone.style.opacity = '1';
      clone.style.transform = 'translateY(0)';
      
      setTimeout(() => {
        isTransitioning = false;
      }, CONFIG.transitionDuration);
    });

    // Marca trigger como ativo
    trigger.setAttribute('aria-expanded', 'true');
    
    // Marca todos os details como fechados visualmente, exceto o atual
    document.querySelectorAll('header-dropdown details').forEach(d => {
      d.removeAttribute('open');
    });
    trigger.closest('details')?.setAttribute('open', '');
  }

  // ============================================================
  // FECHAR DROPDOWN
  // ============================================================
  function closeDropdown(animate = true) {
    if (!portal || !activeDropdown) {
      resetState();
      return;
    }

    const content = portal.querySelector('.dropdown-portal-content');
    
    if (animate && content) {
      isTransitioning = true;
      content.style.opacity = '0';
      content.style.transform = 'translateY(-8px)';
      
      setTimeout(() => {
        resetState();
        isTransitioning = false;
      }, CONFIG.transitionDuration);
    } else {
      resetState();
    }
  }

  function resetState() {
    if (portal) {
      portal.innerHTML = '';
      portal.classList.remove('is-active');
      portal.setAttribute('aria-hidden', 'true');
    }

    if (activeTrigger) {
      activeTrigger.setAttribute('aria-expanded', 'false');
    }
    
    // Fecha todos os details
    document.querySelectorAll('header-dropdown details').forEach(d => {
      d.removeAttribute('open');
    });

    window.removeEventListener('scroll', handleScroll);
    activeDropdown = null;
    activeTrigger = null;
    isHoveringPortal = false;
    isHoveringTrigger = false;
  }

  // ============================================================
  // SCHEDULE CLOSE - Com verificação de hover
  // ============================================================
  function scheduleClose() {
    clearTimeout(hoverTimeout);
    hoverTimeout = setTimeout(() => {
      // Só fecha se não está em hover em nenhum lugar
      if (!isHoveringPortal && !isHoveringTrigger) {
        closeDropdown(true);
      }
    }, CONFIG.hoverDelay);
  }

  // ============================================================
  // EVENT HANDLERS
  // ============================================================
  function handleScroll() {
    const scrollDiff = Math.abs(window.scrollY - scrollStartY);
    if (scrollDiff > CONFIG.closeOnScrollThreshold) {
      clearTimeout(hoverTimeout);
      closeDropdown(true);
    }
  }

  function handleOutsideClick(e) {
    if (!activeDropdown) return;
    
    const clickedPortal = e.target.closest('#' + CONFIG.portalId);
    const clickedTrigger = e.target.closest('header-dropdown');
    
    if (!clickedPortal && !clickedTrigger) {
      clearTimeout(hoverTimeout);
      closeDropdown(true);
    }
  }

  function handleKeydown(e) {
    if (e.key === 'Escape' && activeDropdown) {
      clearTimeout(hoverTimeout);
      closeDropdown(true);
      activeTrigger?.focus();
    }
  }

  // ============================================================
  // INTERCEPTAR DROPDOWNS DO TEMA
  // ============================================================
  function interceptDropdowns() {
    const dropdowns = document.querySelectorAll('header-dropdown');
    
    dropdowns.forEach(dropdown => {
      const details = dropdown.querySelector('details');
      const summary = dropdown.querySelector('summary');
      const content = dropdown.querySelector('.sub-menu-content');
      
      if (!details || !summary || !content) return;

      // Previne comportamento padrão do details
      details.addEventListener('click', (e) => {
        e.preventDefault();
      });

      // Click no summary
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (activeDropdown === content) {
          clearTimeout(hoverTimeout);
          closeDropdown(true);
        } else {
          openDropdown(summary, content);
        }
      });

      // Hover behavior para desktop
      if (window.matchMedia('(hover: hover)').matches) {
        
        // Mouse entra no dropdown trigger
        dropdown.addEventListener('mouseenter', () => {
          isHoveringTrigger = true;
          clearTimeout(hoverTimeout);
          
          // Pequeno delay para evitar abertura acidental
          setTimeout(() => {
            if (isHoveringTrigger) {
              openDropdown(summary, content);
            }
          }, 50);
        });

        // Mouse sai do dropdown trigger
        dropdown.addEventListener('mouseleave', () => {
          isHoveringTrigger = false;
          scheduleClose();
        });
      }
    });

    // Esconde os dropdowns originais via CSS
    if (!document.getElementById('header-dropdown-portal-style')) {
      const style = document.createElement('style');
      style.id = 'header-dropdown-portal-style';
      style.textContent = `
        @media (min-width: 769px) {
          header-dropdown .sub-menu-content {
            display: none !important;
            visibility: hidden !important;
            pointer-events: none !important;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }

  // ============================================================
  // INICIALIZAÇÃO
  // ============================================================
  function init() {
    // Só funciona no desktop
    if (window.innerWidth < 769) return;

    createPortal();
    interceptDropdowns();

    // Re-inicializa em resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        clearTimeout(hoverTimeout);
        closeDropdown(false);
        if (window.innerWidth >= 769) {
          interceptDropdowns();
        }
      }, 250);
    });
  }

  // Aguarda DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();